import React, { useEffect, useMemo, useState } from 'react'
import './ProductsManager.sass'
import ModulePanel from '../../../../../ui/ModulePanel/ModulePanel'
import { withRouter } from 'react-router-dom'
import StatusMessage, { useStatusMessage } from '../../../../../ui/StatusMessage/StatusMessage'
import getDoc from '../../../../../utils/db/getDoc'
import getCollection from '../../../../../utils/db/getCollection'
import { createForm } from '../../../../../utils/newforms/createForm'
import getFieldRenderObject from '../../../../../utils/newforms/render/getFieldRenderObject'
import { isNull, isString, omit } from 'lodash'
import PhotoManager from '../../../../PhotoManager/PhotoManager'
import syncForm from '../../../../../utils/newforms/changed/syncForm'
import Button from '../../../../../ui/Button/Button'
import Spinner from '../../../../../ui/Spinner/Spinner'
import FormRender from '../../../../../utils/newforms/render/FormRender'
import ReadOnlyDetailsCard from '../../../../../ui/ReadOnlyDetailsCard/ReadOnlyDetailsCard'
import Price from '../Price'
import SpecificationsBlock from './SpecificationsBlock/SpecificationsBlock'
import isFormValid from '../../../../../utils/newforms/validation/isFormValid'
import getFormValues from '../../../../../utils/newforms/getFormValues'
import { updateDoc } from '../../../../../utils/db/updateDoc'
import uploadPhotos from '../../../../../_deprecated/Nauka/NaukaManager/functions/uploadPhotos'
import promiseAllValues from '../../../../../utils/promiseAllValues'
import {
  COMPACT_PRODUCT_SEO_FIELDS,
  CompactProductSeoForm,
  PRODUCTS_SEO_OG_STORAGE_PATH,
  ProductSeoSection,
  buildSeoDotPathUpdate,
  deleteSeoOgStorageFile,
  resolveOgImageForSave,
  seoFormDefaultsFromDoc,
} from '../../../seo'
import { CATALOG_TAXONOMY_COLLECTION } from '../../CatalogTaxonomy/catalogTaxonomyConstants'
import {
  buildCatalogTaxonomyNodesById,
  formatProductTaxonomySummary,
} from '../../../productSchema/formatProductTaxonomySummary'
import { readProductTaxonomyFields } from '../../../productSchema/readProductTaxonomyFields'
import { assertProductActivationReadiness } from '../../../productSchema/productTaxonomyFields'
import { buildProductTaxonomyWritePatch } from '../../../productSchema/buildProductTaxonomyWritePatch'
import ProductTaxonomyAssignmentPanel from '../../../productSchema/ProductTaxonomyAssignmentPanel'
import {
  getProductSlugReviewMessage,
  isValidProductSlug,
  slugifyProductName,
} from '../../../productSchema/productSlug'
import saveProductSlug from '../../../../../api/products/saveProductSlug'

function ProductsManager({ ...router }) {
  const { productId } = router.match.params

  const [form, setForm] = useState(null)
  const [seoForm, setSeoForm] = useState(null)
  const [ogImage, setOgImage] = useState(null)
  const [previousOgImage, setPreviousOgImage] = useState(null)
  const [productData, setProductData] = useState(null)
  const [taxonomyNodes, setTaxonomyNodes] = useState([])
  const [assignmentIds, setAssignmentIds] = useState([])
  const [canonicalId, setCanonicalId] = useState(null)
  const [showErrors] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [statusMessage, setStatusMessage] = useStatusMessage()
  const [initialSlug, setInitialSlug] = useState('')

  const nodesById = useMemo(() => buildCatalogTaxonomyNodesById(taxonomyNodes), [taxonomyNodes])

  const taxonomySummary = useMemo(
    () => formatProductTaxonomySummary({ catalogTaxonomyAssignmentIds: assignmentIds, canonicalCatalogTaxonomyNodeId: canonicalId }, nodesById),
    [assignmentIds, canonicalId, nodesById]
  )

  useEffect(() => {
    if (!productId) {
      setForm(null)
      setSeoForm(null)
      setOgImage(null)
      setPreviousOgImage(null)
      setProductData(null)
      setAssignmentIds([])
      setCanonicalId(null)
      setInitialSlug('')
      return undefined
    }

    let cancelled = false
    setForm(null)
    setSeoForm(null)
    setOgImage(null)
    setPreviousOgImage(null)
    setProductData(null)
    setAssignmentIds([])
    setCanonicalId(null)
    setInitialSlug('')

    promiseAllValues({
      product: getDoc({ path: 'products', docId: productId, docIdName: 'id' }),
      taxonomyNodes: getCollection({ path: CATALOG_TAXONOMY_COLLECTION, docIdName: 'id' }),
    })
      .then(({ product, taxonomyNodes: nodes }) =>
        promiseAllValues({
          brandData: getDoc({
            path: 'brands',
            docId: product.brandId,
            docIdName: 'id',
          }),
          product,
          taxonomyNodes: nodes,
        })
      )
      .then((res) => {
        if (cancelled) return
        const seoFormData = seoFormDefaultsFromDoc(res.product.seo, { fields: COMPACT_PRODUCT_SEO_FIELDS })
        const taxonomy = readProductTaxonomyFields(res.product)
        setTaxonomyNodes(res.taxonomyNodes || [])
        setAssignmentIds(taxonomy.catalogTaxonomyAssignmentIds)
        setCanonicalId(taxonomy.canonicalCatalogTaxonomyNodeId)
        setInitialSlug(typeof res.product.slug === 'string' ? res.product.slug : '')
        setProductData({
          ...res.product,
          brandName: res.brandData.name,
        })
        setForm(
          createForm({
            formPattern: new ProductForm(),
            formData: res.product,
          })
        )
        setSeoForm(
          createForm({
            formPattern: new CompactProductSeoForm(),
            formData: seoFormData,
          })
        )
        setOgImage(seoFormData.ogImage || null)
        setPreviousOgImage(seoFormData.ogImage || null)
      })

    return () => {
      cancelled = true
    }
  }, [productId])

  const onSubmit = async () => {
    if (!productId || !form || !seoForm) return

    if (!isFormValid({ form })) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return setStatusMessage({
        show: true,
        type: 'fail',
        message: 'Будь ласка, заповніть усі поля форми',
        closeAfter: 5000,
      })
    }

    const data = getFormValues({ form })
    const willBeActive = data.isActive === true
    const readinessCheck = assertProductActivationReadiness(
      {
        ...(productData || {}),
        ...data,
        catalogTaxonomyAssignmentIds: assignmentIds,
        canonicalCatalogTaxonomyNodeId: canonicalId,
      },
      nodesById
    )

    if (willBeActive && !readinessCheck.ok) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return setStatusMessage({
        show: true,
        type: 'fail',
        message:
          readinessCheck.blockers.map((b) => b.message).join(' ') ||
          'Неможливо зберегти активний товар без доступної основної категорії.',
        closeAfter: 8000,
      })
    }

    setIsLoading(true)
    setStatusMessage({ show: false, type: '', message: '', closeAfter: null })

    let uploadedOrphan = null
    let slugSaved = false

    try {
      const nextSlug = String(data.slug || '').trim().toLowerCase()
      const slugChanged = nextSlug !== String(initialSlug || '').trim()

      if (slugChanged) {
        if (!isValidProductSlug(nextSlug)) {
          window.scrollTo({ top: 0, behavior: 'smooth' })
          setIsLoading(false)
          return setStatusMessage({
            show: true,
            type: 'fail',
            message: 'Некоректний slug. Використовуйте латиницю, цифри та дефіси.',
            closeAfter: 7000,
          })
        }

        const slugResult = await saveProductSlug({ productId, slug: nextSlug })
        if (!slugResult.ok) {
          window.scrollTo({ top: 0, behavior: 'smooth' })
          setIsLoading(false)
          return setStatusMessage({
            show: true,
            type: 'fail',
            message: slugResult.message || 'Slug уже використовується іншим товаром',
            closeAfter: 7000,
          })
        }
        slugSaved = true
        setInitialSlug(nextSlug)
        setForm(
          createForm({
            formPattern: new ProductForm(),
            formData: {
              ...data,
              slug: nextSlug,
              slugNeedsReview: false,
              slugReviewReason: null,
            },
          })
        )
      }

      const taxonomyPatch = buildProductTaxonomyWritePatch({
        assignmentIds,
        previousCanonical: canonicalId,
        nodesById,
        canonicalOverride: canonicalId,
      })

      const values = {
        ...omit(data, [
          'specPrice',
          'specPriceDate',
          'subcategoryId',
          'slug',
          'slugNeedsReview',
          'slugReviewReason',
        ]),
        ...taxonomyPatch,
        specifications: isString(data.specifications.descriptionLong)
          ? data.specifications
          : getFormValues({ form: data.specifications }),
        updated: new Date(),
        photos: (data.photos || []).filter((p) => !p.needUpload).map((p) => omit(p, ['file', 'fileUrl'])),
      }

      const productResult = await updateDoc({ path: 'products', docId: productId, data: values })
      if (productResult === 'fail') {
        throw new Error('product update failed')
      }

      await uploadPhotos({
        photos: data.photos || [],
        docId: productId,
        docPath: 'products',
        storagePath: 'products',
      })

      const seoValues = getFormValues({ form: seoForm })
      const { nextOgImage, uploadedOrphan: orphan, previousToDelete } = await resolveOgImageForSave({
        docId: productId,
        localOgImage: ogImage,
        previousOgImage,
        storagePath: PRODUCTS_SEO_OG_STORAGE_PATH,
      })
      uploadedOrphan = orphan

      const seoPayload = buildSeoDotPathUpdate(
        {
          ...seoValues,
          ogImage: nextOgImage,
        },
        { fields: COMPACT_PRODUCT_SEO_FIELDS }
      )

      const seoResult = await updateDoc({
        path: 'products',
        docId: productId,
        data: seoPayload,
      })

      if (seoResult === 'fail') {
        throw new Error('seo update failed')
      }

      if (previousToDelete) {
        await deleteSeoOgStorageFile({
          docId: productId,
          media: previousToDelete,
          storagePath: PRODUCTS_SEO_OG_STORAGE_PATH,
        })
      }

      if (!nextOgImage && previousOgImage) {
        await deleteSeoOgStorageFile({
          docId: productId,
          media: previousOgImage,
          storagePath: PRODUCTS_SEO_OG_STORAGE_PATH,
        })
      }

      const refreshed = await getDoc({ path: 'products', docId: productId, docIdName: 'id' })
      const seoFormData = seoFormDefaultsFromDoc(refreshed?.seo, { fields: COMPACT_PRODUCT_SEO_FIELDS })
      const taxonomy = readProductTaxonomyFields(refreshed)
      setAssignmentIds(taxonomy.catalogTaxonomyAssignmentIds)
      setCanonicalId(taxonomy.canonicalCatalogTaxonomyNodeId)
      setInitialSlug(typeof refreshed?.slug === 'string' ? refreshed.slug : '')
      setProductData((prev) => ({
        ...refreshed,
        brandName: prev?.brandName,
      }))
      setForm(
        createForm({
          formPattern: new ProductForm(),
          formData: refreshed,
        })
      )
      setSeoForm(
        createForm({
          formPattern: new CompactProductSeoForm(),
          formData: seoFormData,
        })
      )
      setOgImage(seoFormData.ogImage || null)
      setPreviousOgImage(seoFormData.ogImage || null)

      setStatusMessage({
        show: true,
        type: 'success',
        message: 'Дані збережено',
        closeAfter: 5000,
      })
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (error) {
      console.log('🚀 ~ onSubmit ~ error:', error)
      if (uploadedOrphan) {
        await deleteSeoOgStorageFile({
          docId: productId,
          media: uploadedOrphan,
          storagePath: PRODUCTS_SEO_OG_STORAGE_PATH,
        })
      }
      setStatusMessage({
        show: true,
        type: 'fail',
        message: slugSaved
          ? 'Slug збережено, але інші зміни товару не збережено'
          : 'Не вдалося зберегти. Спробуйте ще раз.',
        closeAfter: 7000,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const onRegenerateSlug = () => {
    if (!form) return
    const values = getFormValues({ form })
    const nextSlug = slugifyProductName(values.name)
    setForm(
      createForm({
        formPattern: new ProductForm(),
        formData: { ...values, slug: nextSlug },
      })
    )
  }

  return (
    <div className="ProductsManager">
      {!isNull(form) && !isNull(seoForm) && !isNull(productData) ? (
        <>
          {statusMessage.show && (
            <StatusMessage className="Site-StatusMessage" type={statusMessage.type} message={statusMessage.message} />
          )}
          <ModulePanel title={productData.name}>
            <FormRender
              sections={[
                {
                  fields: ['isActive'],
                },
              ]}
              form={form}
              setForm={setForm}
              errors={showErrors}
            />

            {form?.slugNeedsReview?.values ? (
              <StatusMessage
                className="Site-StatusMessage"
                type="fail"
                message={`Slug потребує перевірки. ${getProductSlugReviewMessage(
                  form.slugReviewReason?.values
                )}`}
              />
            ) : null}

            <ReadOnlyDetailsCard
              title="Основна інформація"
              rows={[
                { label: 'Код товару', value: productData.barcode || '—', mono: true },
                {
                  label: 'Slug',
                  value: form?.slug?.values || productData.slug || '—',
                  mono: true,
                },
                { label: 'Бренд', value: productData.brandName || '—' },
                {
                  label: 'Основна категорія',
                  value: taxonomySummary.mainLine || 'Не вибрано',
                },
                {
                  label: 'Додаткові категорії',
                  value: taxonomySummary.extrasLines.length
                    ? taxonomySummary.extrasLines.join('; ')
                    : '—',
                },
                { label: 'Ціна', value: <Price num={productData.price} /> },
                ...(productData.specPrice != null
                  ? [{ label: 'Акційна ціна', value: <Price num={productData.specPrice} highlighted /> }]
                  : []),
              ]}
            />

            <ProductTaxonomyAssignmentPanel
              assignmentIds={assignmentIds}
              canonicalId={canonicalId}
              nodes={taxonomyNodes}
              nodesById={nodesById}
              productSnapshot={productData}
              onChange={(next) => {
                setAssignmentIds(next.catalogTaxonomyAssignmentIds)
                setCanonicalId(next.canonicalCatalogTaxonomyNodeId)
              }}
            />

            <p className="Title">Додаткова інформація</p>
            <div className="AdditionalInfo-Container">
              <div className="ProductsManager-NameSlugRow">
                <FormRender
                  sections={[
                    {
                      fields: ['name'],
                    },
                  ]}
                  form={form}
                  setForm={setForm}
                  errors={showErrors}
                />
                <div className="ProductsManager-SlugField">
                  <FormRender
                    sections={[{ fields: ['slug'] }]}
                    form={form}
                    setForm={setForm}
                    errors={showErrors}
                  />
                  <Button
                    className="ProductsManager-SlugRegen"
                    theme="circle"
                    fill="white"
                    icon="sync"
                    tooltip="Перегенерувати з назви"
                    onClick={onRegenerateSlug}
                  />
                </div>
              </div>
              <FormRender
                sections={[
                  {
                    fields: ['descriptionShort'],
                  },
                ]}
                form={form}
                setForm={setForm}
                errors={showErrors}
              />
            </div>

            <SpecificationsBlock
              formData={productData.specifications}
              syncState={(moduleState) =>
                syncForm({
                  propIdentifier: 'specifications',
                  form,
                  setForm,
                  newForm: moduleState,
                })
              }
            />
            <p className="Title">Фото товару</p>
            <PhotoManager
              projectId={productId}
              projectPath="products"
              formData={form ? form.photos.values : null}
              syncState={(moduleState) =>
                syncForm({
                  propIdentifier: 'photos',
                  form,
                  setForm,
                  newForm: moduleState,
                })
              }
            />

            <ProductSeoSection
              form={seoForm}
              setForm={setSeoForm}
              ogImage={ogImage}
              setOgImage={setOgImage}
            />
          </ModulePanel>
          <div className="Buttons">
            <Button
              title="Зберегти"
              theme="solid"
              fill="accent"
              iconPosition="right"
              size={32}
              isLoading={isLoading}
              onClick={onSubmit}
            />
          </div>
        </>
      ) : (
        <Spinner type="module" />
      )}
    </div>
  )
}

class ProductForm {
  constructor() {
    this.name = {
      field: {
        fieldId: 'name',
        fieldType: 'input',
        inputType: 'text',
        label: 'Назва товару',
        required: false,
      },
      render: getFieldRenderObject(),
    }
    this.slug = {
      field: {
        fieldId: 'slug',
        fieldType: 'input',
        inputType: 'text',
        label: 'Slug',
        required: false,
      },
      render: getFieldRenderObject(),
    }
    this.slugNeedsReview = {
      field: { fieldId: 'slugNeedsReview', value: false },
      render: getFieldRenderObject({ isSystem: true }),
    }
    this.slugReviewReason = {
      field: { fieldId: 'slugReviewReason', value: null },
      render: getFieldRenderObject({ isSystem: true }),
    }
    this.descriptionShort = {
      field: {
        fieldId: 'descriptionShort',
        fieldType: 'textarea',
        label: 'Короткий опис товару',
        required: false,
      },
      render: getFieldRenderObject(),
    }
    this.specifications = {
      field: { fieldId: 'specifications', value: {} },
      render: getFieldRenderObject({ isSystem: true }),
    }

    this.photos = {
      field: { fieldId: 'photos', value: [] },
      render: getFieldRenderObject({ isSystem: true }),
    }

    this.created = {
      field: { fieldId: 'created', value: new Date() },
      render: getFieldRenderObject({ isSystem: true }),
    }
    this.isActive = {
      field: {
        fieldId: 'isActive',
        fieldType: 'checkbox',
        label: 'Активний',
        value: true,
      },
      render: getFieldRenderObject(),
    }
  }
}

export default withRouter(ProductsManager)
